import { useEffect, useRef } from 'react';
import {
  getControlForKey,
  getInputStateFromKeys,
} from './inputState';

export function useKeyboardControls({
  enabled = true,
  onCancel,
  onConfirm,
  onInputChange,
  preventDefault = true,
}) {
  const pressedKeysRef = useRef(new Set());
  const callbacksRef = useRef({
    onCancel,
    onConfirm,
    onInputChange,
  });

  useEffect(() => {
    callbacksRef.current = {
      onCancel,
      onConfirm,
      onInputChange,
    };
  }, [onCancel, onConfirm, onInputChange]);

  useEffect(() => {
    if (!enabled) {
      pressedKeysRef.current = new Set();
      callbacksRef.current.onInputChange?.(getInputStateFromKeys([]));
      return undefined;
    }

    const emitInput = () => {
      callbacksRef.current.onInputChange?.(getInputStateFromKeys(pressedKeysRef.current));
    };

    const onKeyDown = (event) => {
      const control = getControlForKey(event.key);

      if (!control) {
        return;
      }

      if (preventDefault) {
        event.preventDefault();
      }

      const normalizedKey = event.key.toLowerCase();
      const wasPressed = pressedKeysRef.current.has(normalizedKey);
      pressedKeysRef.current.add(normalizedKey);
      emitInput();

      if (wasPressed || event.repeat) {
        return;
      }

      if (control === 'confirm' || control === 'primary') {
        callbacksRef.current.onConfirm?.();
      }

      if (control === 'cancel') {
        callbacksRef.current.onCancel?.();
      }
    };

    const onKeyUp = (event) => {
      const control = getControlForKey(event.key);

      if (!control) {
        return;
      }

      if (preventDefault) {
        event.preventDefault();
      }

      pressedKeysRef.current.delete(event.key.toLowerCase());
      emitInput();
    };

    const onBlur = () => {
      pressedKeysRef.current = new Set();
      emitInput();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      pressedKeysRef.current = new Set();
      emitInput();
    };
  }, [enabled, preventDefault]);
}
